import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { getCurrentUser } from "../../utils/Api";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import Config from "../../Config";

import FreePlanUpgradeModal from "../../modals/FreePlanUpgradeModal";
import DeleteAccountModal, {
  OrganizationToDelete,
} from "../../modals/DeleteAccountModal";
import UserNavbar from "../../components/UserNavbar";
import Footer from "../../components/Footer";

interface SubscriptionStatus {
  subscription_id: string | null;
  status: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
}

const Settings: React.FC = () => {
  const { user, logout } = useAuth();

  const [pagesUploadedToday, setPagesUploadedToday] = useState<number>(0);
  const [dailyLimit, setDailyLimit] = useState<number>(10);
  const [accountType, setAccountType] = useState<string>("free");
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null,
  );
  const [portalLoading, setPortalLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [organizationsToDelete, setOrganizationsToDelete] = useState<
    OrganizationToDelete[]
  >([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const response = await getCurrentUser();
        setAccountType(response.data.account_type);
        setPagesUploadedToday(response.data.pages_uploaded_today);
        setDailyLimit(response.data.daily_limit);
      } catch (error) {
        console.error("Failed to load user info:", error);
      }
    };
    loadUserInfo();
  }, []);

  useEffect(() => {
    const loadSubscription = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      try {
        const res = await fetch(`${Config.API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSubscription(await res.json());
      } catch {}
    };
    loadSubscription();
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    const token = localStorage.getItem("access_token");
    try {
      const res = await fetch(`${Config.API_URL}/api/billing/portal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const getResetTime = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const diffMs = tomorrow.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMins}m`;
  };

  const fetchOrganizationsToDelete = async () => {
    setLoadingOrganizations(true);
    try {
      const backendToken = localStorage.getItem("access_token");
      if (!backendToken) {
        return;
      }

      const response = await fetch(
        `${Config.API_URL}/api/auth/organizations-to-delete`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${backendToken}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setOrganizationsToDelete(data.organizations || []);
      }
    } catch (error) {
      console.error("Error fetching organizations to delete:", error);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${Config.API_URL}/api/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my-data.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalOpen(true);
    fetchOrganizationsToDelete();
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !user.email) {
      setDeleteError("User not authenticated");
      return;
    }

    if (!isPsdiUser && !deletePassword) {
      setDeleteError("Please enter your password to confirm");
      return;
    }

    setDeleteLoading(true);
    setDeleteError("");

    try {
      if (!isPsdiUser) {
        const credential = EmailAuthProvider.credential(
          user.email,
          deletePassword,
        );
        await reauthenticateWithCredential(user, credential);
      }

      const backendToken = localStorage.getItem("access_token");

      if (!backendToken) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${Config.API_URL}/api/auth/delete-account`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${backendToken}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          throw new Error(
            errorData.detail ||
              "Cannot delete account due to organization admin requirements",
          );
        }
        throw new Error(
          errorData.detail || "Failed to delete account from backend",
        );
      }

      if (!isPsdiUser && user.uid) {
        const userDocRef = doc(db, "users", user.uid);
        await deleteDoc(userDocRef);
        await deleteUser(user);
      }

      await logout();
      window.location.href = "/";
    } catch (error: any) {
      console.error("Error deleting account:", error);
      if (
        error.code === "auth/invalid-credential" ||
        error.code === "auth/wrong-password"
      ) {
        setDeleteError("Incorrect password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        setDeleteError("Too many failed attempts. Please try again later.");
      } else {
        setDeleteError(
          error.message || "Failed to delete account. Please try again.",
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-20 mb-10 text-left">
        <h3 className="text-2xl font-semibold">Settings</h3>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const isPsdiUser = user?.uid === "keycloak-user";

  return (
    <div className="flex flex-col min-h-screen">
      <UserNavbar />
      <div className="flex-1 w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-20 mb-20 text-left">
        <h3 className="text-2xl font-semibold">Settings</h3>
        <p className="text-gray-700">Manage your account settings</p>
        <hr className="my-4 border-gray-300" />

        {/* usage (for free, individual, and PSDI accounts) */}
        {(accountType === "free" ||
          accountType === "individual" ||
          isPsdiUser) && (
          <>
            <h6 className="text-base font-semibold mt-6">Usage</h6>
            <div className="border border-gray-300 rounded p-4 mt-3">
              <div className="flex items-center gap-6 max-[600px]:flex-col max-[600px]:items-stretch max-[600px]:gap-3">
                <div className="flex flex-col flex-shrink-0 max-[600px]:flex-row max-[600px]:justify-between max-[600px]:items-center">
                  <span className="font-medium">Pages today</span>
                  <span className="text-sm text-gray-500">
                    Resets in {getResetTime()}
                  </span>
                </div>
                <div className="flex-1 max-[600px]:flex max-[600px]:items-center max-[600px]:gap-3">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#1d365d] rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min((pagesUploadedToday / dailyLimit) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-gray-700 flex-shrink-0 hidden max-[600px]:block">
                    {pagesUploadedToday}/{dailyLimit}
                  </span>
                </div>
                <span className="text-gray-700 flex-shrink-0 max-[600px]:hidden">
                  {pagesUploadedToday}/{dailyLimit}
                </span>
              </div>
              {accountType === "free" && !isPsdiUser && (
                <p className="text-sm text-gray-500 mt-3">
                  Want more pages? Upgrade to Individual.{" "}
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    Learn more
                  </button>
                </p>
              )}
            </div>
          </>
        )}

        {/* subscription */}
        <h6 className="text-base font-semibold mt-6">Subscription</h6>
        <div className="border border-gray-300 rounded p-4 mt-3 space-y-4">
          {accountType === "individual" ? (
            <>
              <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                <span className="text-gray-700">Plan</span>
                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-md text-sm font-medium">
                  Individual (£25/month)
                </span>
              </div>
              <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                <span className="text-gray-700">Status</span>
                <span
                  className={`px-3 py-1 rounded-md text-sm font-medium capitalize ${
                    subscription?.cancel_at_period_end
                      ? "bg-orange-50 text-orange-700"
                      : subscription?.status === "active"
                        ? "bg-green-50 text-green-700"
                        : subscription?.status === "past_due"
                          ? "bg-yellow-50 text-yellow-700"
                          : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {subscription?.cancel_at_period_end
                    ? "Cancelling"
                    : (subscription?.status ?? "active")}
                </span>
              </div>
              {subscription?.current_period_end && (
                <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                  <span className="text-gray-700">
                    {subscription.cancel_at_period_end
                      ? "Plan ends on"
                      : "Next billing date"}
                  </span>
                  <span className="text-gray-700 text-sm">
                    {new Date(
                      subscription.current_period_end * 1000,
                    ).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              )}
              <div className="pt-1">
                <button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="px-4 py-2 text-sm font-medium text-[#1d365d] border border-[#1d365d] rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {portalLoading ? "Opening..." : "Manage subscription"}
                </button>
                <p className="text-xs text-gray-400 mt-1.5">
                  Cancel, update payment method or view invoices via Stripe
                </p>
              </div>
            </>
          ) : accountType === "organization" ? (
            <p className="text-sm text-gray-500">
              Your subscription is managed by your organisation admin.
            </p>
          ) : (
            <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
              <div>
                <span className="font-medium text-gray-700">Free plan</span>
                <p className="text-sm text-gray-500 mt-0.5">
                  Upgrade to unlock 100 pages/day and all features
                </p>
              </div>
              <Link
                to="/upgrade"
                className="px-4 py-2 text-sm font-medium text-white bg-[#1d365d] rounded hover:bg-[#16294a] transition-colors whitespace-nowrap"
              >
                Upgrade to individual
              </Link>
            </div>
          )}
        </div>

        {/* account */}
        <h6 className="text-base font-semibold mt-4">Account</h6>
        <div className="border border-gray-300 rounded p-4 mt-3">
          {!isPsdiUser && (
            <>
              <div className="flex items-center max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1 mb-4">
                <div className="flex-1">Account created</div>
                <div className="text-gray-700">
                  {user.metadata?.creationTime
                    ? new Date(user.metadata.creationTime).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                    : "N/A"}
                </div>
              </div>
              <div className="flex items-center max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                <div className="flex-1">Last sign in</div>
                <div className="text-gray-700">
                  {user.metadata?.lastSignInTime
                    ? new Date(user.metadata.lastSignInTime).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )
                    : "N/A"}
                </div>
              </div>
            </>
          )}
        </div>

        {/* data & privacy */}
        <h6 className="text-base font-semibold mt-6">Data &amp; privacy</h6>
        <div className="border border-gray-300 rounded p-4 mt-3">
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
            <div>
              <span className="font-medium text-gray-700">
                Download your data
              </span>
              <p className="text-sm text-gray-500 mt-0.5">
                Export a copy of all data we hold about you
              </p>
            </div>
            <button
              onClick={handleExportData}
              disabled={exportLoading}
              className="px-4 py-2 text-sm font-medium text-[#1d365d] border border-[#1d365d] rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {exportLoading ? "Preparing..." : "Download your data"}
            </button>
          </div>
        </div>

        {/* danger zone */}
        <h6 className="text-base font-semibold mt-6">Danger zone</h6>
        <div className="border border-red-200 rounded p-4 mt-3 bg-red-50">
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
            <div>
              <span className="font-medium text-red-700">Delete account</span>
              <p className="text-sm text-red-600 mt-1">
                Permanently delete your account and all associated data
              </p>
            </div>
            <button
              className="px-4 py-2 text-red-600 border border-red-300 rounded hover:bg-red-100 transition-colors text-sm font-medium max-[600px]:w-full"
              onClick={handleOpenDeleteModal}
            >
              Delete account
            </button>
          </div>
        </div>

        <div className="text-center mt-10">
          <p className="text-gray-500">
            Made with care for your lab notes • Data Revival • v2025.12.09.1
          </p>
        </div>

        <FreePlanUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
        <DeleteAccountModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setDeletePassword("");
            setDeleteError("");
          }}
          onSubmit={handleDeleteAccount}
          deletePassword={deletePassword}
          setDeletePassword={setDeletePassword}
          deleteError={deleteError}
          deleteLoading={deleteLoading}
          organizationsToDelete={organizationsToDelete}
          loadingOrganizations={loadingOrganizations}
          isPsdiUser={isPsdiUser}
        />
      </div>
      <Footer />
    </div>
  );
};

export default Settings;
